import { useQuery } from "@apollo/client";

import {
  ITrophyDefinition,
  ITrophyProgress,
  TrophyKey,
  TrophyTier,
} from "MVVM/@types/trophies";
import { GET_TROPHY_PROGRESS } from "MVVM/GraphQL/trophies";
import { getTrophyProgress } from "MVVM/Utilities/Trophy";
import { PREVIEW_TROPHY_PROGRESS } from "MVVM/Utilities/Rewards/preview";

// Mirrors quentin's helpers/trophyGain: each tier held across these adds a clip to
// a free player's limit, capped at TROPHY_GAIN_CAP.
export const LIMIT_TROPHIES = [
  TrophyKey.CRT_CLIPS,
  TrophyKey.STRK_DAILY_CLAIM,
  TrophyKey.COMP_PARTICIPATE,
  TrophyKey.CRT_COMMAND,
];
export const TROPHY_GAIN_CAP = 5;

export type TLimitTrophy = {
  key: TrophyKey;
  name: string;
  // The definition's "Create X clips", with X as the next tier's target.
  goal: string | null;
  tier: TrophyTier | null;
  value: number;
  progress: ITrophyProgress | null;
};

export const stepsToNextTier = ({ progress }: TLimitTrophy) =>
  progress ? Math.max(progress.target - progress.current, 0) : 0;

type TReading = { key: string; tier: TrophyTier | null; value: number | null };

export const useLimitTrophies = ({
  handle,
  trophyGain,
  skip = false,
  preview = false,
}: {
  handle?: string | null;
  trophyGain?: number | null;
  skip?: boolean;
  preview?: boolean;
}) => {
  const query = useQuery(GET_TROPHY_PROGRESS, {
    variables: { query: handle },
    skip: skip || preview || !handle,
    fetchPolicy: "cache-and-network",
  });
  const data = preview ? PREVIEW_TROPHY_PROGRESS : query.data;

  const readings: TReading[] = data?.profile?.trophyProgress ?? [];
  const definitions: ITrophyDefinition[] = data?.trophyDefinitions ?? [];

  const trophies: TLimitTrophy[] = LIMIT_TROPHIES.map((key) => {
    const reading = readings.find((r) => r.key === key);
    const definition = definitions.find((d) => d.key === key);
    const tier = reading?.tier ?? null;
    const value = reading?.value ?? 0;
    // A null tier has index -1, so the next tier is bronze.
    const progress = getTrophyProgress(
      { key, tier: tier as TrophyTier, value },
      definitions,
    );
    return {
      key,
      name: definition?.name ?? key,
      goal:
        progress && definition
          ? definition.description.replace("X", String(progress.target))
          : null,
      tier,
      value,
      progress,
    };
  });

  const gain = Math.min(trophyGain ?? 0, TROPHY_GAIN_CAP);
  const maxed = gain >= TROPHY_GAIN_CAP;
  // Fewest steps to the next tier. Nothing is closest once tiers stop adding clips.
  const closest = maxed
    ? null
    : trophies
        .filter((trophy) => trophy.progress)
        .reduce<TLimitTrophy | null>(
          (best, trophy) =>
            !best || stepsToNextTier(trophy) < stepsToNextTier(best)
              ? trophy
              : best,
          null,
        );

  return {
    trophies,
    gain,
    maxed,
    closest,
    loading: query.loading && !data,
    loaded: !!data,
  };
};
